import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Truck, Store } from "lucide-react";
import { api, imageUrl } from "../lib/api";

const PLACEHOLDER = "https://customer-assets.emergentagent.com/job_store-hub-157/artifacts/yyc18991_IMG_4514.jpeg";

export function AdminRestockBuilder({ onCreated }) {
  const [vendors, setVendors] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [vendorStock, setVendorStock] = useState({});
  const [vendorId, setVendorId] = useState("");
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);

  const loadCatalog = useCallback(() => api.get("/admin/catalog").then((r) => setCatalog(r.data.filter((c) => c.active))).catch(console.error), []);
  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data.filter((v) => !v.is_national))).catch(console.error);
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (!vendorId) { setVendorStock({}); return; }
    api.get(`/vendors/${vendorId}/products`)
      .then((r) => setVendorStock(Object.fromEntries((r.data || []).map((p) => [p.id, p.quantity ?? 0]))))
      .catch(() => setVendorStock({}));
  }, [vendorId]);

  const byCat = useMemo(() => {
    const m = {};
    catalog.forEach((c) => { (m[c.category || "Autres"] ||= []).push(c); });
    return Object.entries(m);
  }, [catalog]);

  const lines = Object.entries(qty).filter(([, v]) => (parseInt(v) || 0) > 0).map(([catalog_id, v]) => ({ catalog_id, quantity: parseInt(v) }));
  const total = lines.reduce((s, l) => s + l.quantity, 0);
  const vendor = vendors.find((v) => v.id === vendorId);

  const setQ = (c, v) => {
    const n = Math.max(0, parseInt(v) || 0);
    const fs = c.factory_stock || 0;
    if (n > fs) { toast.error(`Stock usine insuffisant pour ${c.name} (dispo : ${fs})`); setQty((q) => ({ ...q, [c.id]: fs })); return; }
    setQty((q) => ({ ...q, [c.id]: n || "" }));
  };

  const create = async () => {
    if (!vendorId) { toast.error("Choisis une boutique"); return; }
    if (!lines.length) { toast.error("Indique au moins une quantité"); return; }
    setBusy(true);
    try {
      await api.post("/admin/restock", { vendor_id: vendorId, items: lines });
      toast.success(`Réassort créé pour ${vendor?.name} — ${total} unités soustraites du stock usine`);
      setQty({}); loadCatalog(); onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-ink-surface border border-zinc-800 p-4" data-testid="admin-restock-create">
      <h3 className="font-anton text-lg uppercase text-white mb-1">Préparer un réassort</h3>
      <p className="text-zinc-500 text-xs mb-3">1. Choisis la boutique · 2. Saisis les quantités directement dans la liste · 3. Crée le bordereau. Le stock usine est soustrait immédiatement ; au « Livré », il est ajouté à la boutique.</p>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="restock-vendor-chips">
        {vendors.map((v) => (
          <button key={v.id} type="button" data-testid="restock-vendor-chip" onClick={() => { setVendorId(v.id); setQty({}); }}
            className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 border transition-colors ${vendorId === v.id ? "bg-neon-magenta border-neon-magenta text-white" : "border-zinc-700 text-zinc-300 hover:border-neon-magenta"}`}>
            <Store className="h-3.5 w-3.5" /> {v.name} <span className="opacity-60 font-normal">· {v.city}</span>
          </button>
        ))}
      </div>

      {!vendorId ? (
        <p className="text-zinc-600 text-sm border border-dashed border-zinc-800 p-4 text-center" data-testid="restock-pick-vendor-hint">👆 Sélectionne une boutique pour afficher la liste des produits.</p>
      ) : (
        <>
          <div className="space-y-4" data-testid="restock-product-list">
            {byCat.map(([cat, items]) => (
              <div key={cat}>
                <p className="text-neon-green text-[11px] uppercase font-bold tracking-widest mb-1.5">{cat}</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  {items.map((c) => {
                    const fs = c.factory_stock || 0;
                    const vs = vendorStock[c.id] ?? 0;
                    return (
                      <div key={c.id} data-testid="restock-product-row" className={`flex items-center gap-3 bg-ink border p-2.5 ${qty[c.id] > 0 ? "border-neon-green/60" : "border-zinc-800"}`}>
                        <img src={imageUrl(c.image_url) || PLACEHOLDER} alt="" className="h-11 w-11 object-cover shrink-0" onError={(e) => { e.target.src = PLACEHOLDER; }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold truncate">{c.name}</p>
                          <p className="text-[11px] text-zinc-500">
                            usine : <span className={fs <= 5 ? "text-red-400 font-bold" : "text-zinc-300"} data-testid="restock-factory-stock">{fs}</span>
                            <span className="mx-1.5">·</span>
                            boutique : <span className={vs <= 3 ? "text-yellow-400 font-bold" : "text-zinc-300"} data-testid="restock-vendor-stock">{vs}</span>
                          </p>
                        </div>
                        <input
                          data-testid="restock-qty-input"
                          type="number" min="0" max={fs} placeholder="0" inputMode="numeric"
                          value={qty[c.id] || ""}
                          disabled={fs <= 0}
                          onChange={(e) => setQ(c, e.target.value)}
                          className="w-20 bg-ink-surface border border-zinc-700 text-white px-2 py-2 text-center focus:outline-none focus:border-neon-magenta disabled:opacity-30"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="sticky bottom-3 mt-4 flex items-center gap-3 bg-ink-surface/95 backdrop-blur border border-zinc-700 p-3">
            <span className="text-zinc-400 text-sm">{lines.length} produit{lines.length > 1 ? "s" : ""} · <span className="text-white font-anton">{total}</span> unités → <span className="text-neon-magenta font-bold">{vendor?.name}</span></span>
            <button data-testid="restock-create-btn" onClick={create} disabled={busy || total === 0} className="ml-auto inline-flex items-center gap-2 bg-neon-magenta text-white font-anton uppercase px-5 py-2.5 disabled:opacity-40">
              <Truck className="h-4 w-4" /> Créer le réassort
            </button>
          </div>
        </>
      )}
    </div>
  );
}
